import { useContinuousVoice } from "./continuous-voice-store";
import {
  classifyTtsEcho,
  type EchoSuppressionResult,
  isLikelyTtsAcknowledgementEcho,
  isShortAnswerToSpokenQuestion,
  stripLeadingEcho,
  stripSpokenPrefix,
} from "./voice-echo-suppression";
import { getActiveTtsTextForEcho, getSpokenTtsTextsForEcho } from "./use-tts";
import { detectLiveVoiceControlIntent } from "./continuous-voice-farewell";
import { continuousVoiceTtsEpoch } from "./continuous-voice-tts-epoch";

const SCOPED_TTS_ECHO_MARKER_MS = 30_000;
const LEGACY_TTS_ECHO_MARKER_MS = 1_500;
const DUPLICATE_TURN_SUPPRESSION_MS = 45_000;
const SHORT_DUPLICATE_TURN_SUPPRESSION_MS = 120_000;

/** Letters, marks and digits of any script survive, so a turn in Russian or
 *  Japanese is compared as itself instead of as an empty string. */
function normalizeTurnText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{M}\p{N}_\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Whether `text` repeats the turn just sent. Once Denker has spoken since,
 * the same words answer something new ("Send it?" "Okay." "Archive it too?"
 * "Okay."), so only the same speech sent twice is a duplicate.
 */
export function shouldSuppressDuplicateTurn(text: string): boolean {
  const normalized = normalizeTurnText(text);
  if (!normalized) return true;
  const state = useContinuousVoice.getState();
  const previous = normalizeTurnText(state.lastDispatchStartedText ?? "");
  if (!previous || previous !== normalized) return false;
  const startedAt = state.lastDispatchStartedAt;
  if (!startedAt || getSpokenTtsTextsForEcho(startedAt, Date.now()).length)
    return false;
  const windowMs =
    normalized.split(" ").length <= 2
      ? SHORT_DUPLICATE_TURN_SUPPRESSION_MS
      : DUPLICATE_TURN_SUPPRESSION_MS;
  return Date.now() - startedAt < windowMs;
}

type NativeTranscriptIdentity = {
  captureId?: string;
  segmentId?: number;
};

function getRecentSuppressedTtsEcho(
  identity?: NativeTranscriptIdentity,
): { text: string } | null {
  const state = useContinuousVoice.getState();
  if (!state.lastSuppressedTtsEchoText || !state.lastSuppressedTtsEchoAt) {
    return null;
  }
  const markerHasIdentity =
    state.lastSuppressedTtsEchoCaptureId !== null &&
    state.lastSuppressedTtsEchoSegmentId !== null;
  const transcriptHasIdentity =
    identity?.captureId !== undefined && identity.segmentId !== undefined;
  if (
    markerHasIdentity &&
    transcriptHasIdentity &&
    (state.lastSuppressedTtsEchoCaptureId !== identity.captureId ||
      state.lastSuppressedTtsEchoSegmentId !== identity.segmentId)
  ) {
    return null;
  }
  const markerAgeMs = Date.now() - state.lastSuppressedTtsEchoAt;
  const maxAgeMs =
    markerHasIdentity && transcriptHasIdentity
      ? SCOPED_TTS_ECHO_MARKER_MS
      : LEGACY_TTS_ECHO_MARKER_MS;
  if (markerAgeMs > maxAgeMs) return null;
  return { text: state.lastSuppressedTtsEchoText };
}

/**
 * What the latest Whisper final said after Denker's echo was cut from its
 * start. Those words followed Denker's, so they are the user's: the
 * word-overlap echo test would read a short answer that reuses the
 * question's words ("yes, the blue one") as echo again.
 */
let userWordsAfterEcho: string | null = null;

/** Denker's voice still reaches the microphone this long after it ends. */
const ECHO_TAIL_MS = 1_000;

/** When the speech being judged began and ended (its audio's end). */
export type SpeechSpan = { startedAt: number; endedAt: number };

/**
 * The one echo window: what Denker said that `span` of speech can hold -
 * what played during it, or ended up to the echo tail before it began, but
 * never a passage begun after it ended. A word said after Denker finished is
 * the user's, however much of Denker's speech it repeats. An unknown (0)
 * bound is now: nothing beyond what plays now overlaps it.
 */
function spokenDuring({ startedAt, endedAt }: SpeechSpan): string[] {
  const now = Date.now();
  return getSpokenTtsTextsForEcho(
    (startedAt || now) - ECHO_TAIL_MS,
    endedAt || now,
  );
}

/**
 * When the live segment's speech began: as the detector heard it, else when
 * the playback now suspending the detector began (the segment holds all of
 * it), else now.
 */
export function liveSpeechStartedAt(): number {
  return (
    useContinuousVoice.getState().segmentStartedAt ??
    continuousVoiceTtsEpoch.suspendedSince() ??
    Date.now()
  );
}

/** The live segment's speech, up to now. */
function liveSpeechSpan(): SpeechSpan {
  return { startedAt: liveSpeechStartedAt(), endedAt: Date.now() };
}

/** Best outcome across all recently spoken passages. */
function classifyAgainstAll(
  text: string,
  spokenTexts: string[],
): EchoSuppressionResult | "no_tts" {
  if (!spokenTexts.length) return "no_tts";
  let best: EchoSuppressionResult = "divergent";
  for (const spoken of spokenTexts) {
    const result = classifyTtsEcho(text, spoken);
    if (result === "echo" || result === "empty") return result;
    if (result === "uncertain") best = "uncertain";
  }
  return best;
}

export function ttsEchoDebugProps(text: string, span = liveSpeechSpan()) {
  const spokenTexts = spokenDuring(span);
  const activeText = getActiveTtsTextForEcho();
  const markedText = getRecentSuppressedTtsEcho()?.text ?? null;
  const spokenText = spokenTexts[0] ?? markedText;
  return {
    transcript_length: text.trim().length,
    transcript_words: normalizeTurnText(text).split(" ").filter(Boolean).length,
    tts_source: activeText
      ? "active"
      : spokenTexts.length
        ? "recent"
        : markedText
          ? "marked"
          : "none",
    tts_length: spokenText?.trim().length ?? 0,
    tts_passages: spokenTexts.length,
    echo: spokenText ? classifyAgainstAll(text, spokenTexts) : "no_tts",
    acknowledgement_echo: isLikelyTtsAcknowledgementEcho(text),
  };
}

export function shouldSuppressMarkedTtsEcho(
  text: string,
  identity?: NativeTranscriptIdentity,
): boolean {
  const markedText = getRecentSuppressedTtsEcho(identity)?.text;
  if (!markedText) return false;
  const transcriptVsMarked = classifyTtsEcho(text, markedText);
  return transcriptVsMarked === "echo" || transcriptVsMarked === "empty";
}

/** Whether `text`, said over `span`, is Denker's own voice. */
export function shouldSuppressTtsTranscript(
  text: string,
  span = liveSpeechSpan(),
): boolean {
  if (text.trim() === userWordsAfterEcho) return false;
  const spokenTexts = spokenDuring(span);
  if (!spokenTexts.length) return false;
  if (
    spokenTexts.some((spoken) => isShortAnswerToSpokenQuestion(text, spoken))
  ) {
    return false;
  }
  const echo = classifyAgainstAll(text, spokenTexts);
  return (
    echo === "echo" || echo === "empty" || isLikelyTtsAcknowledgementEcho(text)
  );
}

/** Below this many characters, text without a control intent is a short
 *  answer or a fragment, never the user taking the turn from Denker. */
const REAL_WORDS_MIN_CHARS = 8;

/**
 * The one rule for whether the user's speech stops Denker, at a reply's start
 * and in the middle of one: a control intent ("stop", …), a short answer to a
 * question Denker just asked, or a sentence that is not Denker's own words.
 * "Yes", "ok" and other short words over anything else never do, nor does
 * "send" or "go", which commit the user's own turn.
 */
export function isRealUserWords(text: string): boolean {
  const words = text.trim();
  const control = detectLiveVoiceControlIntent(words);
  if (control && control.intent !== "commit_now") return true;
  const spokenTexts = spokenDuring(liveSpeechSpan());
  if (
    spokenTexts.some((spoken) => isShortAnswerToSpokenQuestion(words, spoken))
  )
    return true;
  if (words.length < REAL_WORDS_MIN_CHARS) return false;
  if (words === userWordsAfterEcho) return true;
  return (
    !spokenTexts.length ||
    classifyAgainstAll(words, spokenTexts) === "divergent"
  );
}

/**
 * One-shot (Ctrl+Opt) captures have no partial-gating: when the user barges
 * in over Denker, the capture starts with our own speaker audio. Strip that
 * leading echo so it is not dispatched as a prompt; "" means pure echo.
 * Only what Denker said while this was recorded (`recorded`, from the key
 * press to its release) can be in it: an "okay" said after Denker finished
 * is the user's own reply.
 * `heard` is the transcript before Rust's vocabulary corrected it (sent only
 * when it did): a corrected word no longer matches what Denker said, so the
 * echo is stripped from the text as heard, and only when that strips
 * something is the correction lost.
 */
export function stripTtsEchoFromOneShotFinal(
  transcript: string,
  recorded: SpeechSpan,
  heard?: string,
): string {
  const spokenTexts = spokenDuring(recorded);
  if (!spokenTexts.length) return transcript;
  if (isLikelyTtsAcknowledgementEcho(transcript)) return "";
  const asHeard = heard?.trim();
  if (asHeard && asHeard !== transcript) {
    if (isLikelyTtsAcknowledgementEcho(asHeard)) return "";
    const stripped = stripSpokenPrefix(asHeard, spokenTexts);
    if (stripped !== null && stripped !== asHeard) return stripped;
  }
  return stripSpokenPrefix(transcript, spokenTexts) ?? transcript;
}

/**
 * A transcribe-at-pause final (Whisper) covers the whole phrase, so one that
 * began while Denker spoke starts with its cancelled voice before the user's
 * words. That leading echo is dropped so the rest is judged on its own; ""
 * when all of it was Denker's.
 */
export function stripLeadingTtsEchoFromFinal(
  transcript: string,
  span = liveSpeechSpan(),
): string {
  const spokenTexts = spokenDuring(span);
  const remainder = spokenTexts.length
    ? stripLeadingEcho(transcript, spokenTexts)
    : transcript;
  userWordsAfterEcho =
    remainder !== transcript && remainder.trim() ? remainder.trim() : null;
  return remainder;
}
